import React, { useCallback, useEffect, useState } from 'react'

import { Box, Chip } from '@mui/material'
import { useSelector } from 'react-redux'

import EmptyState from '../EmptyState/EmptyState.component'
import Table from '../Table/Table.component'

import env from '../../../networkRequests/apiClient/env.config'
import {
  getPurchaseReadyItems,
  selectPurchaseReadyItemsForStatus
} from '../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.slice'
import {
  GetPurchaseReadyItemsRequestPayload,
  PurchaseReadyItemRecord
} from '../../../redux/states/admin/getPurchaseReadyItems/getPurchaseReadyItems.types'
import date, { dateTimeFormats } from '../../../shared/functions/Date/date.functions'
import { getStatusColor } from '../../../shared/functions/Status/status.functions'
import { formatMoney } from '../../../shared/functions/String/string.functions'
import { useAppDispatch } from '../../../shared/hooks/redux.hooks'
import { TableColumn } from '../Table/Table.types'
import { FilterStatus, apiStatusForFilter } from './ReadyToPurchaseTable.dictionary'
import styles from './ReadyToPurchaseTable.styles'
import type { ReadyToPurchaseTableProps } from './ReadyToPurchaseTable.types'

const ReadyToPurchaseTable: React.FC<ReadyToPurchaseTableProps> = (props: ReadyToPurchaseTableProps) => {
  const { onRowClick, filterStatus, searchQuery } = props

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(env.DEFAULT_PAGE_SIZE)

  const dispatch = useAppDispatch()

  // The `status` this tab queries, and therefore the key its results live under.
  // Derived once so the read below and the request further down can never drift
  // apart — all four tabs are mounted at the same time, and reading another
  // tab's key would render another tab's rows.
  const apiStatus = apiStatusForFilter(filterStatus)

  const { loading, success, error } = useSelector(selectPurchaseReadyItemsForStatus(apiStatus))

  const handleGetPurchaseReadyItems = useCallback(
    (page: number = 1) => {
      const payload: GetPurchaseReadyItemsRequestPayload = {
        request: {
          page,
          size: pageSize,
          status: apiStatus,
          searchQuery
        }
      }

      dispatch(getPurchaseReadyItems(payload))
    },
    [dispatch, pageSize, apiStatus, searchQuery]
  )

  useEffect(() => {
    handleGetPurchaseReadyItems(currentPage)
  }, [handleGetPurchaseReadyItems, currentPage])

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage)
  }, [])

  // Filter data based on filterStatus
  const getFilteredData = () => {
    if (!success?.data.records || success.data.records.length === 0) return []

    // The API should already filter based on status, so we just flatten the data
    return success.data.records.flatMap((record: PurchaseReadyItemRecord) => {
      if (!record.offerRequestDetails || !record.offerRequestDetails.itemRequests) {
        return []
      }

      return record.offerRequestDetails.itemRequests.map((itemRequest) => ({
        ...itemRequest,
        offerRequestDetails: record.offerRequestDetails
      }))
    })
  }

  const flattenedData = getFilteredData()

  const tableColumns: TableColumn<(typeof flattenedData)[0]>[] = [
    {
      key: 'item',
      label: 'Item',
      render: (row) => (
        <Box sx={styles.itemContainer}>
          <Box
            component="img"
            src={row.itemDetails?.images?.[0] || ''}
            alt={row.itemDetails?.productName || ''}
            sx={styles.itemImage}
          />

          <Box sx={styles.itemName}>{row.itemDetails?.productName}</Box>
          {/*
            "Item ID", not "ID". This is the CATALOGUE id and is shared by every
            order for the same product, so it reads like a row identifier and is
            not one — two shoppers ordering the same thing produce rows identical
            in every visible field. The Order / Request column carries the actual
            identity.
          */}
          <Box sx={styles.itemId}>Item ID: {row.itemDetails?.itemId}</Box>
        </Box>
      )
    },
    {
      // The only column that tells two otherwise-identical rows apart: the order
      // an admin would refer to, and the item request that IS this row.
      key: 'reference',
      label: 'Order / Request',
      render: (row) => (
        <Box sx={styles.reference}>
          Order #{row.offerRequestDetails?.id}
          <br />
          Request #{row.id}
        </Box>
      )
    },
    {
      key: 'quantity',
      label: 'Quantity',
      render: (row) => row.quantity
    },
    {
      key: 'price',
      label: 'Price',
      render: (row) => formatMoney(row.acceptedItemPrice, 'USD')
    },
    {
      key: 'deliveryAddress',
      label: 'Delivery Address',
      render: (row) => (
        <Box sx={styles.deliveryAddress}>
          {row.offerRequestDetails?.deliveryDetails?.deliveryAddress?.street}
          <br />
          {row.offerRequestDetails?.deliveryDetails?.deliveryAddress?.city},{' '}
          {row.offerRequestDetails?.deliveryDetails?.deliveryAddress?.country}{' '}
          {row.offerRequestDetails?.deliveryDetails?.deliveryAddress?.postalCode}
        </Box>
      )
    },
    {
      key: 'deliveryWindow',
      label: 'Delivery Window',
      render: (row) => {
        const startDate = row.offerRequestDetails?.deliveryDetails?.deliveryWindow?.startDate
        const endDate = row.offerRequestDetails?.deliveryDetails?.deliveryWindow?.endDate

        return (
          <Box sx={styles.deliveryWindow}>
            {startDate ? date(startDate).format(dateTimeFormats.date.medium) : 'N/A'}
            <br />
            to
            <br />
            {endDate ? date(endDate).format(dateTimeFormats.date.medium) : 'N/A'}
          </Box>
        )
      }
    },
    {
      key: 'itemStatus',
      label: 'Item Status',
      render: (row) => (
        <Chip label={row.status?.displayName || 'Unknown'} color={getStatusColor(row.status?.name)} size="small" />
      )
    },
    {
      key: 'traveler',
      label: 'Traveler',
      render: (row) => (
        <Box sx={styles.travelerInfo}>
          {row.offerRequestDetails?.travelerDetails?.firstName} {row.offerRequestDetails?.travelerDetails?.lastName}
          <br />
          {row.offerRequestDetails?.travelerDetails?.email}
        </Box>
      )
    }
  ]

  if (filterStatus === FilterStatus.readyForPurchase && (!flattenedData || flattenedData.length === 0) && !loading) {
    return (
      <Box sx={styles.container}>
        <EmptyState title="No Items Ready for Purchase" subtitle="Items ready for purchase will appear here." />
      </Box>
    )
  }

  // Show empty state if no data
  if (filterStatus === FilterStatus.purchased && (!flattenedData || flattenedData.length === 0) && !loading) {
    return (
      <Box sx={styles.container}>
        <EmptyState subtitle="Purchased items that are awaiting delivery confirmation will appear here." />
      </Box>
    )
  }

  if (
    filterStatus === FilterStatus.deliveredToTravelerAddress &&
    (!flattenedData || flattenedData.length === 0) &&
    !loading
  ) {
    return (
      <Box sx={styles.container}>
        <EmptyState
          title="No Items Delivered to Traveler"
          subtitle="Items delivered to traveler address will appear here after delivery is confirmed by an admin or the traveler."
          icon="local_shipping"
        />
      </Box>
    )
  }

  if (filterStatus === FilterStatus.deliveredToShopper && (!flattenedData || flattenedData.length === 0) && !loading) {
    return (
      <Box sx={styles.container}>
        <EmptyState
          title="No Items Delivered to Shopper"
          subtitle="Items delivered to shopper will appear here after delivery is confirmed by an admin or the shopper."
          icon="local_shipping"
        />
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      <Table
        columns={tableColumns}
        data={flattenedData}
        onRowClick={onRowClick}
        // Stable handles for anything driving this table. Without them the only
        // way to target a specific order's row is its position in the response,
        // which stops being reliable the moment a record carries several item
        // requests or the list spans more than one page.
        rowAttributes={(row) => ({
          'data-offer-request-id': row.offerRequestDetails?.id,
          'data-item-request-id': row.id
        })}
        pagination={{
          currentPage: success?.data.currentPage || 1,
          totalPages: success?.data.totalPages || 1,
          totalRecords: success?.data.totalRecords || 0,
          onNext: () => handlePageChange(currentPage + 1),
          onPrevious: () => handlePageChange(currentPage - 1)
        }}
        initialLoading={loading}
        error={!!error}
        onRetry={() => handleGetPurchaseReadyItems(currentPage)}
        inPage
      />
    </Box>
  )
}

export default ReadyToPurchaseTable
